// Training-day banner — only renders when /api/runtime returns a
// non-empty ``training_mode``. Use Heroku config:set TRAINING_MODE
// to a human-readable date string ("2026-05-04") to switch it on,
// or unset to hide.
//
// The banner is sticky-top, full-width, and uses the warm amber
// palette so trainees know they're in the right environment without
// it competing visually with the Nav (Salesforce blue) or any toast
// notifications.

import { useRuntime } from "@/hooks/useRuntime";

const TRAINING_DATE_PRETTY: Record<string, string> = {
  "2026-05-04": "May 4, 2026 · Tableau Cloud Sales/Service Insights training",
};

export function TrainingBanner(): JSX.Element | null {
  const { runtime, isTrainingDay } = useRuntime();
  if (!isTrainingDay || !runtime?.training_mode) return null;

  const pretty = TRAINING_DATE_PRETTY[runtime.training_mode] ?? runtime.training_mode;
  const attendees = runtime.attendee_count
    ? `${runtime.attendee_count} attendees`
    : null;
  const browserOff = runtime.browser_automation_disabled;

  return (
    <div
      role="status"
      aria-label="Training mode banner"
      style={{
        background:
          "linear-gradient(90deg, #f59e0b 0%, #f59e0b 70%, #fbbf24 100%)",
        color: "#1a1a1a",
        padding: "6px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        fontSize: 12,
        fontWeight: 500,
        boxShadow: "inset 0 -1px 0 rgba(0,0,0,.18)",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      <span style={{ fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", fontSize: 11 }}>
        Training Mode
      </span>
      <span style={{ opacity: 0.9 }}>· {pretty}</span>
      {attendees && (
        <span
          style={{
            opacity: 0.9,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          · {attendees}
        </span>
      )}
      {browserOff && (
        <span
          style={{
            opacity: 0.9,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          · Stream refresh = manual UI step
        </span>
      )}
      {runtime.release_version && (
        <span
          style={{
            opacity: 0.7,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 11,
          }}
        >
          {runtime.release_version}
        </span>
      )}
    </div>
  );
}
